import React, { createContext, useContext, useMemo, useReducer } from 'react';

const Context = createContext();

const globalState = {
  cartOpen: false,
  modal: { children: null, props: {} },
  drawer: { children: null, props: {} },
  overlayOpen: false,
  searchOpen: false,
  accountOpen: false,
  testVariant: 'original',
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'OPEN_CART':
      return {
        ...state,
        cartOpen: true,
        modal: { children: null, props: {} },
        drawer: { children: null, props: {} },
        overlayOpen: true,
        searchOpen: false,
      };
    case 'CLOSE_CART':
      return {
        ...state,
        cartOpen: false,
        overlayOpen: false,
      };
    case 'OPEN_MODAL':
      return {
        ...state,
        cartOpen: false,
        modal: {
          children: action.payload.children,
          props: action.payload.props,
        },
        overlayOpen: true,
        searchOpen: false,
      };
    case 'CLOSE_MODAL':
      return {
        ...state,
        modal: { children: null, props: {} },
        overlayOpen: false,
      };
    case 'OPEN_ACCOUNT':
      return {
        ...state,
        cartOpen: false,
        modal: { children: null, props: {} },
        drawer: { children: null, props: {} },
        overlayOpen: true,
        accountOpen: true,
      };
    case 'CLOSE_ACCOUNT':
      return {
        ...state,
        overlayOpen: false,
        accountOpen: false,
      };
    case 'OPEN_DRAWER':
      return {
        ...state,
        cartOpen: false,
        drawer: {
          children: action.payload.children,
          props: action.payload.props,
        },
        overlayOpen: true,
        searchOpen: false,
        accountOpen: false,
      };
    case 'CLOSE_DRAWER':
      return {
        ...state,
        drawer: { children: null, props: {} },
        overlayOpen: false,
      };
    case 'CLOSE_OVERLAY':
      return {
        ...state,
        cartOpen: false,
        modal: { children: null, props: {} },
        drawer: { children: null, props: {} },
        overlayOpen: false,
        searchOpen: false,
        accountOpen: false,
      };
    case 'OPEN_SEARCH':
      return {
        ...state,
        cartOpen: false,
        modal: { children: null, props: {} },
        drawer: { children: null, props: {} },
        overlayOpen: true,
        searchOpen: true,
        accountOpen: false,
      };
    case 'CLOSE_SEARCH':
      return {
        ...state,
        overlayOpen: false,
        searchOpen: false,
        accountOpen: false,
      };
    case 'SET_TEST_VARIANT':
      return {
        ...state,
        testVariant: action.payload.name,
      };
    default:
      throw new Error(`Invalid Context action of type: ${action.type}`);
  }
};

const globalActions = (dispatch) => ({
  openCart: () => {
    dispatch({ type: 'OPEN_CART' });
  },
  closeCart: () => {
    dispatch({ type: 'CLOSE_CART' });
  },
  openAccount: () => {
    dispatch({ type: 'OPEN_ACCOUNT' });
  },
  closeAccount: () => {
    dispatch({ type: 'CLOSE_ACCOUNT' });
  },
  openModal: (children, props) => {
    dispatch({ type: 'OPEN_MODAL', payload: { children, props } });
  },
  closeModal: () => {
    dispatch({ type: 'CLOSE_MODAL' });
  },
  openDrawer: (children, props) => {
    dispatch({ type: 'OPEN_DRAWER', payload: { children, props } });
  },
  closeDrawer: () => {
    dispatch({ type: 'CLOSE_DRAWER' });
  },
  closeOverlay: () => {
    dispatch({ type: 'CLOSE_OVERLAY' });
  },
  openSearch: () => {
    dispatch({ type: 'OPEN_SEARCH' });
  },
  closeSearch: () => {
    dispatch({ type: 'CLOSE_SEARCH' });
  },
  setTestVariant: (name) => {
    dispatch({ type: 'SET_TEST_VARIANT', payload: { name } });
  },
});

export function GlobalContextProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, { ...globalState });

  const value = useMemo(
    () => ({ state, actions: globalActions(dispatch) }),
    [state]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export const useGlobalContext = () => useContext(Context);
